// Browser- and device-level context for the step log: who is scanning on what.
// Split into several records because a record carries at most ten fields.
// Best-effort throughout; a browser that hides something just omits it.

import { mark } from "./stepLog.js";

/** The browser, its version and the OS, from client hints or the UA string. */
export function parseUA(ua = "") {
  const pick = (re) => (ua.match(re) || [])[1] || "";
  const browser = /EdgA?\//.test(ua) ? "edge" : /SamsungBrowser\//.test(ua) ? "samsung" : /Firefox\/|FxiOS\//.test(ua) ? "firefox"
    : /CriOS\//.test(ua) ? "chrome-ios" : /Chrome\//.test(ua) ? "chrome" : /Safari\//.test(ua) ? "safari" : "other";
  const version = pick(/(?:EdgA?|SamsungBrowser|Firefox|FxiOS|CriOS|Chrome|Version)\/([\d.]+)/).split(".")[0];
  const os = /Android/.test(ua) ? "android" : /iPhone|iPad|iPod/.test(ua) ? "ios" : /Mac OS X/.test(ua) ? "mac" : /Windows/.test(ua) ? "windows" : /Linux/.test(ua) ? "linux" : "other";
  const osVersion = pick(/Android ([\d.]+)/) || pick(/OS ([\d_]+) like Mac/).replace(/_/g, ".");
  const model = pick(/Android [\d.]+; ([^;)]+)[;)]/).trim();
  return { browser, version, os, osVersion, model };
}

/** Any iOS browser (all WebKit), iPadOS posing as a Mac included. */
export function isIOS(ua = typeof navigator !== "undefined" ? navigator.userAgent : "", touchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0) {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
}

function net() {
  const c = typeof navigator !== "undefined" ? navigator.connection : null;
  return c ? { type: c.effectiveType || "", downlink: c.downlink, rtt: c.rtt, save: Boolean(c.saveData), online: navigator.onLine } : { online: navigator.onLine };
}

/** Once per page load: browser, hardware, network, capabilities. */
export async function logDevice() {
  try {
    const ua = parseUA(navigator.userAgent);
    let brand = "";
    let model = ua.model;
    let platformVersion = ua.osVersion;
    try {
      const d = navigator.userAgentData;
      if (d) {
        const b = (d.brands || []).find((x) => !/Not.?A.?Brand|Chromium/i.test(x.brand));
        brand = b ? `${b.brand} ${b.version}` : "";
        const h = await d.getHighEntropyValues?.(["model", "platformVersion"]);
        if (h?.model) model = h.model;
        if (h?.platformVersion) platformVersion = h.platformVersion;
      }
    } catch {
      /* hints refused */
    }
    mark("device_browser", { browser: ua.browser, version: ua.version, brand, os: ua.os, osv: platformVersion, model, standalone: window.matchMedia?.("(display-mode: standalone)").matches || false, lang: navigator.language || "" });
    mark("device_hw", { cores: navigator.hardwareConcurrency || 0, mem: navigator.deviceMemory || 0, sw: screen.width, sh: screen.height, dpr: window.devicePixelRatio || 1, vw: window.innerWidth, vh: window.innerHeight, touch: navigator.maxTouchPoints || 0 });
    mark("device_net", net());
    let formats = "";
    try {
      formats = typeof BarcodeDetector === "function" ? (await BarcodeDetector.getSupportedFormats()).length : -1;
    } catch {
      formats = -2;
    }
    let cam = "";
    try {
      cam = (await navigator.permissions?.query({ name: "camera" }))?.state || "";
    } catch {
      cam = "n/a";
    }
    mark("device_caps", { barcodeDetector: typeof BarcodeDetector === "function", bdFormats: formats, worker: typeof Worker === "function", offscreen: typeof OffscreenCanvas === "function", imageBitmap: typeof createImageBitmap === "function", camPermission: cam, webp: true });
  } catch {
    /* never let logging break the page */
  }
}

/** Network changes, script errors and failed promises, as they happen. */
export function watchDevice() {
  try {
    navigator.connection?.addEventListener?.("change", () => mark("net_change", net()));
    window.addEventListener("online", () => mark("net_change", net()));
    window.addEventListener("offline", () => mark("net_change", net()));
    window.addEventListener("error", (e) => {
      const src = String(e.filename || "").split("/").pop();
      mark("js_error", { msg: String(e.message || "error").slice(0, 40), src: `${src}:${e.lineno || 0}` });
    });
    window.addEventListener("unhandledrejection", (e) => {
      const r = e.reason;
      mark("js_error", { msg: String((r && (r.name || r.message)) || r || "rejection").slice(0, 40), kind: "promise" });
    });
    document.addEventListener("visibilitychange", () => mark("visibility", { state: document.visibilityState }));
  } catch {
    /* ignore */
  }
}
